import React, { useEffect } from 'react';
import { Alert, App, Button, Card, Col, Form, Input, InputNumber, Row, Space, Spin, Typography } from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { FormattedTextArea } from '@/components/form/FormattedTextArea';
import { PaymentTermSelect } from '@/components/finance/PaymentTermSelect';
import {
  useCanManageQuotationDefaults,
  useQuotationDefaults,
  useUpdateQuotationDefaults,
} from '@/hooks/useQuotationDefaults';
import type { QuotationDefaults } from '@/services/quotationDefaults.service';
import { escapePlainTextAsHtml, looksLikeHtml, sanitizeRichTextHtml } from '@/utils/richText';

const { Title, Paragraph, Text } = Typography;

function previewHtml(value: string | undefined, fallback: string): string {
  const v = String(value ?? '').trim();
  const source = v && v.replace(/<[^>]+>/g, '').trim() ? v : fallback;
  return looksLikeHtml(source) ? sanitizeRichTextHtml(source) : escapePlainTextAsHtml(source);
}

/** Sales → Masters → Quotation defaults: presets for new quotations and the print cover letter. */
const QuotationDefaultsPage: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm<QuotationDefaults>();
  const canManage = useCanManageQuotationDefaults();
  const { data, isLoading } = useQuotationDefaults();
  const updateMutation = useUpdateQuotationDefaults();
  const builtIn = data?.built_in;

  useEffect(() => {
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  const salutation = Form.useWatch('print_salutation', form);
  const intro = Form.useWatch('print_intro', form);
  const introRental = Form.useWatch('print_intro_rental', form);

  const handleSave = async () => {
    const values = await form.validateFields();
    try {
      await updateMutation.mutateAsync({ ...values, validity_days: values.validity_days ?? null });
      message.success('Quotation defaults saved');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save quotation defaults');
    }
  };

  const applyStandard = (field: 'print_salutation' | 'print_intro' | 'print_intro_rental') => {
    if (builtIn) form.setFieldValue(field, builtIn[field]);
  };

  if (isLoading) return <Spin style={{ display: 'block', margin: 48 }} />;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} align="start">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Quotation defaults
          </Title>
          <Text type="secondary">
            Pre-filled on every new sales quotation (from BOQ, estimation or trading / rental estimation) and used on the
            quotation print.
          </Text>
        </div>
        {canManage && (
          <Button type="primary" icon={<SaveOutlined />} loading={updateMutation.isPending} onClick={handleSave}>
            Save
          </Button>
        )}
      </Space>

      {!canManage && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Read only — you need the “Manage Quotation Defaults” permission to change these."
        />
      )}

      <Form form={form} layout="vertical" disabled={!canManage}>
        <Row gutter={16}>
          <Col xs={24} xl={14}>
            <Card title="Defaults for new quotations" style={{ marginBottom: 16 }}>
              <Paragraph type="secondary" style={{ marginTop: -4 }}>
                Applied only when the quotation is created and the field is empty. Terms coming from the source
                estimation win, and each quotation can still be edited afterwards. Existing quotations are not changed.
              </Paragraph>
              <Row gutter={16}>
                <Col xs={24} md={16}>
                  <Form.Item label="Payment Terms" name="payment_terms">
                    <PaymentTermSelect allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Validity (days)"
                    name="validity_days"
                    tooltip="Used when the source estimation has no price validity. Leave empty for 30 days."
                  >
                    <InputNumber min={1} max={3650} precision={0} style={{ width: '100%' }} placeholder="30" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Delivery Terms" name="delivery_terms">
                <FormattedTextArea disabled={!canManage} minHeight={72} placeholder="Default delivery terms (paste keeps formatting)" />
              </Form.Item>
              <Form.Item
                label="Notes"
                name="notes"
                tooltip="Replaces the automatic “Created from …” note; notes written on the source are kept."
              >
                <FormattedTextArea disabled={!canManage} minHeight={96} placeholder="Default notes (paste keeps formatting)" />
              </Form.Item>
              <Form.Item label="Terms and Conditions" name="terms_and_conditions">
                <FormattedTextArea disabled={!canManage} minHeight={160} placeholder="Default terms and conditions (paste keeps formatting)" />
              </Form.Item>
              <Form.Item label="Special Instructions" name="special_instructions">
                <FormattedTextArea disabled={!canManage} minHeight={72} placeholder="Default special instructions (paste keeps formatting)" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} xl={10}>
            <Card title="Print cover letter" style={{ marginBottom: 16 }}>
              <Paragraph type="secondary" style={{ marginTop: -4 }}>
                Printed above the item table on every quotation PDF and print, including existing quotations. Leave a
                field empty to use the standard wording.
              </Paragraph>
              <Form.Item
                label={
                  <Space>
                    Greeting
                    {canManage && (
                      <Button size="small" type="link" icon={<UndoOutlined />} onClick={() => applyStandard('print_salutation')}>
                        Standard
                      </Button>
                    )}
                  </Space>
                }
                name="print_salutation"
              >
                <Input maxLength={200} placeholder={builtIn?.print_salutation} />
              </Form.Item>
              <Form.Item
                label={
                  <Space>
                    Introduction
                    {canManage && (
                      <Button size="small" type="link" icon={<UndoOutlined />} onClick={() => applyStandard('print_intro')}>
                        Standard
                      </Button>
                    )}
                  </Space>
                }
                name="print_intro"
                extra="Trading, manufacturing and mixed trading / rental quotations."
              >
                <FormattedTextArea disabled={!canManage} minHeight={88} placeholder={builtIn?.print_intro} />
              </Form.Item>
              <Form.Item
                label={
                  <Space>
                    Introduction — rental only
                    {canManage && (
                      <Button size="small" type="link" icon={<UndoOutlined />} onClick={() => applyStandard('print_intro_rental')}>
                        Standard
                      </Button>
                    )}
                  </Space>
                }
                name="print_intro_rental"
                extra="Quotations where every line is a rental."
              >
                <FormattedTextArea disabled={!canManage} minHeight={72} placeholder={builtIn?.print_intro_rental} />
              </Form.Item>
            </Card>

            <Card title="Preview" size="small">
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                <p style={{ marginBottom: 4 }}>{String(salutation ?? '').trim() || builtIn?.print_salutation}</p>
                <div dangerouslySetInnerHTML={{ __html: previewHtml(intro, builtIn?.print_intro ?? '') }} />
                <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                  Rental-only quotations:
                </Text>
                <div dangerouslySetInnerHTML={{ __html: previewHtml(introRental, builtIn?.print_intro_rental ?? '') }} />
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default QuotationDefaultsPage;
